import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Alert, Box, Button, ButtonGroup, Typography } from "@mui/material";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

type PdfPreviewProps = {
  url: string;
  onLoadSuccess?: () => void;
  onLoadError?: () => void;
};

export default function PdfPreview({ url, onLoadSuccess, onLoadError }: PdfPreviewProps) {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setNumPages(undefined);
    setPageNumber(1);
    setLoadError(false);
  }, [url]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setLoadError(false);
  }
  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      {containerWidth > 0 && (
        <Document
          key={url}
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={() => {
            setLoadError(true);
            setNumPages(undefined);
            onLoadError?.();
          }}
          error={null}
          options={options}
        >
          {!loadError && (
            <Page
              pageNumber={pageNumber}
              width={Math.min(containerWidth - 48, 800)}
              onRenderSuccess={onLoadSuccess}
              onRenderError={() => {
                setLoadError(true);
                onLoadError?.();
              }}
            />
          )}
        </Document>
      )}
      {loadError ? (
        <Alert severity="error" sx={{ width: "100%" }}>
          {t("document.review.pdfError")}
        </Alert>
      ) : (
        <>
          {numPages && (
            <Typography variant="body2" color="text.secondary">
              {t("document.review.pageCount", { page: pageNumber, total: numPages })}
            </Typography>
          )}
          {numPages && numPages > 1 && (
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setPageNumber(pageNumber - 1)}
                disabled={pageNumber === 1}
                aria-label={t("document.review.previousPage")}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setPageNumber(pageNumber + 1)}
                disabled={pageNumber >= numPages}
                aria-label={t("document.review.nextPage")}
              >
                <ArrowForwardIcon />
              </Button>
            </ButtonGroup>
          )}
        </>
      )}
    </Box>
  );
}
